import type { BooleanInput } from '@angular/cdk/coercion';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  Renderer2,
  signal,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideX } from '@ng-icons/lucide';
import {
  injectExposedSideProvider,
  injectExposesStateProvider,
} from '@spartan-ng/brain/core';
import { HlmButton } from '@spartan-ng/helm/button';

import { classes } from '@spartan-ng/helm/utils';
import { HlmSheetClose } from './hlm-sheet-close';

@Component({
  selector: 'hlm-sheet-content',
  imports: [HlmButton, HlmSheetClose, NgIcon],
  providers: [provideIcons({ lucideX })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-slot': 'sheet-content',
    '[attr.data-side]': '_sideProvider.side()',
    '[attr.data-state]': 'state()',
  },
  template: `
    <ng-content />

    @if (showCloseButton()) {
      <button
        hlmBtn
        variant="ghost"
        size="icon-sm"
        class="absolute end-4 top-4"
        hlmSheetClose
      >
        <span class="sr-only">Close</span>
        <ng-icon name="lucideX" />
      </button>
    }
  `,
})
export class HlmSheetContent {
  private readonly _stateProvider = injectExposesStateProvider({ host: true });
  protected readonly _sideProvider = injectExposedSideProvider({ host: true });
  public readonly state = this._stateProvider.state ?? signal('closed');
  private readonly _renderer = inject(Renderer2);
  private readonly _element = inject(ElementRef);

  public readonly showCloseButton = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });

  constructor() {
    classes(() => [
      'bg-popover text-popover-foreground fixed flex flex-col gap-4 bg-clip-padding text-sm transition duration-200 ease-in-out data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm',
      // pt-BR: canto arredondado no lado que "entra" na tela + elevação do DS
      // (--radius-xl é o token de bottom sheet; --shadow-sheet sobe a sombra).
      // Substitui o shadow-lg do helm upstream, que é enviesado para baixo e
      // some atrás da borda da viewport num sheet ancorado embaixo.
      // O border-t/border-b do upstream sai junto: com o canto arredondado ele
      // vira um risco escuro cortando o raio, e a elevação já separa do fundo.
      'shadow-sheet data-[side=bottom]:rounded-t-xl data-[side=top]:rounded-b-xl',
      // pt-BR: variantes em `data-[state=…]` e não `data-open:`/`data-closed:`.
      // O host só escreve `data-state` (ver o effect abaixo), então as variantes
      // do helm upstream nunca casavam e o sheet aparecia seco, sem animação.
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[side=top]:data-[state=closed]:slide-out-to-top data-[side=top]:data-[state=open]:slide-in-from-top',
      'data-[side=bottom]:data-[state=closed]:slide-out-to-bottom data-[side=bottom]:data-[state=open]:slide-in-from-bottom',
      'data-[side=left]:data-[state=closed]:slide-out-to-left data-[side=left]:data-[state=open]:slide-in-from-left',
      'data-[side=right]:data-[state=closed]:slide-out-to-right data-[side=right]:data-[state=open]:slide-in-from-right',
    ]);
    effect(() => {
      this._renderer.setAttribute(
        this._element.nativeElement,
        'data-state',
        this.state(),
      );
    });
  }
}
